/**
 * Kompaktes Aktionsmenü ("⋯") für Listen-Zeilen.
 *
 * Ersetzt mehrere nebeneinander stehende Buttons durch ein einzelnes
 * Overflow-Menü — deutlich aufgeräumter, besonders auf schmalen Handys.
 * Schließt bei Klick außerhalb und bei Escape.
 */

import { useEffect, useRef, useState } from 'react';

export interface RowAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export function RowActionsMenu({
  actions,
  label = 'Aktionen',
}: {
  actions: RowAction[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost !min-h-[36px] w-9 rounded text-xl leading-none"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        ⋯
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-30 min-w-[168px] rounded-lg border border-border bg-surface-2 shadow-xl overflow-hidden"
        >
          {actions.map((a, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={a.disabled}
              onClick={() => {
                setOpen(false);
                a.onClick();
              }}
              className={[
                'block w-full text-left px-3.5 py-2.5 text-sm !min-h-0 transition disabled:opacity-40',
                a.variant === 'danger'
                  ? 'text-red-300 hover:bg-red-600/20'
                  : 'text-slate-100 hover:bg-white/5',
              ].join(' ')}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
